import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  Collapse,
  Chip,
  useTheme,
  useMediaQuery,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { formatCelular } from '../../utils';
import { THEME_COLORS, BREAKPOINTS } from '../../constants';
import SubscriptionFormModal from '../forms/SubscriptionFormModal';
import { useSubscriptions } from '../../hooks';

/**
 * Componente de tabla de contactos para vista de escritorio
 */
const ContactsTable = ({
  contacts,
  expandedContactId,
  onExpand,
  onEdit,
  onDelete,
  showActions = true,
  // Props de paginación
  pagination = null,
  onPageChange = null,
  onPageSizeChange = null,
  // Callback para refrescar datos después de crear suscripción
  onRefresh = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.MOBILE));
  
  // Estados para el modal de suscripción
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [selectedContactForSubscription, setSelectedContactForSubscription] = useState(null);
  
  // Hook de suscripciones para obtener contactos disponibles y crear suscripción
  const { availableContacts, createSubscription } = useSubscriptions();

  // Función para abrir el modal de suscripción con un contacto preseleccionado
  const handleOpenSubscriptionModal = (contact) => {
    // Crear un objeto de contacto compatible con el modal de suscripción
    const contactForModal = {
      contact_id: contact.contact_id,
      nombre: contact.nombre,
      celular: contact.celular,
      email_ewelink: contact.email_ewelink
    };
    
    setSelectedContactForSubscription(contactForModal);
    setSubscriptionModalOpen(true);
  };

  // Función para cerrar el modal de suscripción
  const handleCloseSubscriptionModal = () => {
    setSubscriptionModalOpen(false);
    setSelectedContactForSubscription(null);
  };

  // Función para crear suscripción
  const handleCreateSubscription = async (subscriptionData) => {
    try {
      const result = await createSubscription(subscriptionData);
      if (result.success) {
        handleCloseSubscriptionModal();
        // Refrescar los datos de contactos si se proporciona la función
        if (onRefresh) {
          onRefresh();
        }
      }
      return result;
    } catch (error) {
      console.error('Error creando suscripción:', error);
      return { success: false, message: error.message };
    }
  };

  // Componente de controles de paginación
  const PaginationControls = () => {
    if (!pagination) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 2,
          borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
        }}
      >
        {/* Información de registros */}
        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
          Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} contactos
        </Typography>

        {/* Controles de navegación */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Selector de elementos por página */}
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={pagination.limit}
              onChange={(e) => onPageSizeChange?.(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                },
                '& .MuiSelect-select': {
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '0.875rem',
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>

          {/* Botones de navegación */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!pagination.hasPrev}
              onClick={() => onPageChange?.(pagination.page - 1)}
              startIcon={<NavigateBeforeIcon />}
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                borderColor: THEME_COLORS.BORDER_LIGHT,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: THEME_COLORS.TEXT_PRIMARY,
                  color: THEME_COLORS.TEXT_PRIMARY,
                },
                '&:disabled': {
                  color: THEME_COLORS.TEXT_MUTED,
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                },
              }}
            >
              Anterior
            </Button>

            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, px: 2 }}>
              Página {pagination.page} de {pagination.pages || 1}
            </Typography>

            <Button
              variant="outlined"
              size="small"
              disabled={!pagination.hasNext}
              onClick={() => onPageChange?.(pagination.page + 1)}
              endIcon={<NavigateNextIcon />}
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                borderColor: THEME_COLORS.BORDER_LIGHT,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: THEME_COLORS.TEXT_PRIMARY,
                  color: THEME_COLORS.TEXT_PRIMARY,
                },
                '&:disabled': {
                  color: THEME_COLORS.TEXT_MUTED,
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                },
              }}
            >
              Siguiente
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  if (contacts.length === 0) {
    return (
      <Box>
        <TableContainer 
          component={Paper} 
          sx={{ 
            border: `1px solid ${THEME_COLORS.BORDER}`,
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableBody>
              <TableRow>
                <TableCell colSpan={showActions ? 7 : 6} sx={{ textAlign: 'center', padding: 4 }}>
                  <Typography variant="body1" sx={{ color: THEME_COLORS.TEXT_MUTED }}>
                    No se encontraron contactos
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <PaginationControls />
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer 
        component={Paper} 
        sx={{ 
          border: `1px solid ${THEME_COLORS.BORDER}`,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: THEME_COLORS.LIGHT_GRAY }}>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Contacto</TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Celular</TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Email eWeLink</TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Distribuidor</TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Ubicación</TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Suscripción</TableCell>
              {showActions && (
                <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY, textAlign: 'center' }}>
                  Acciones
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <React.Fragment key={contact.contact_id}>
                {/* Fila principal */}
                <TableRow
                  sx={{
                    '&:hover': {
                      backgroundColor: THEME_COLORS.LIGHT_GRAY,
                    },
                  }}
                >
                  {/* Contacto */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: THEME_COLORS.TEXT_PRIMARY,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        >
                          {contact.nombre}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: THEME_COLORS.TEXT_MUTED,
                            fontSize: '0.75rem',
                          }}
                        >
                          ID: {contact.contact_id.slice(0, 8)}...
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => onExpand(contact.contact_id)}
                        sx={{ marginLeft: 1, color: THEME_COLORS.SECONDARY }}
                      >
                        {expandedContactId === contact.contact_id ? <ExpandMoreIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </TableCell>

                  {/* Celular */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY }} />
                      <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY }}>
                        {formatCelular(contact.celular)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Email eWeLink */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY }} />
                      <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY }}>
                        {contact.email_ewelink || 'No configurado'}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Distribuidor */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY }} />
                      <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY }}>
                        {contact.distributor_empresa ? `${contact.distributor_empresa}` : 'Sin asignar'}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Ubicación */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY }} />
                      <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY }}>
                        {contact.ubicacion || 'No especificada'}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Suscripción */}
                  <TableCell>
                    {(() => {
                      const getSubscriptionChip = () => {
                        switch (contact.subscriptionStatusDetail) {
                          case 'active':
                            return (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label="Activa"
                                size="small"
                                sx={{
                                  backgroundColor: '#1a1a1a',
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  border: 'none',
                                  '& .MuiChip-icon': {
                                    color: 'white',
                                    fontSize: '16px',
                                  },
                                  '& .MuiChip-label': {
                                    paddingLeft: '8px',
                                    paddingRight: '12px',
                                  },
                                  boxShadow: '0 2px 4px rgba(26, 26, 26, 0.3)',
                                }}
                              />
                            );
                          
                          case 'expired':
                            return (
                              <Tooltip title="Click para reactivar suscripción" arrow>
                                <Chip
                                  icon={<CancelIcon />}
                                  label="Vencida"
                                  size="small"
                                  clickable
                                  onClick={() => handleOpenSubscriptionModal(contact)}
                                  sx={{
                                    backgroundColor: '#9ca3af',
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '& .MuiChip-icon': {
                                      color: 'white',
                                      fontSize: '16px',
                                    },
                                    '& .MuiChip-label': {
                                      paddingLeft: '8px',
                                      paddingRight: '12px',
                                    },
                                    boxShadow: '0 2px 4px rgba(156, 163, 175, 0.3)',
                                    '&:hover': {
                                      backgroundColor: '#6b7280',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 4px 8px rgba(156, 163, 175, 0.4)',
                                    },
                                  }}
                                />
                              </Tooltip>
                            );
                          
                          case 'cancelled':
                            return (
                              <Tooltip title="Click para crear nueva suscripción" arrow>
                                <Chip
                                  icon={<CancelIcon />}
                                  label="Cancelada"
                                  size="small"
                                  clickable
                                  onClick={() => handleOpenSubscriptionModal(contact)}
                                  sx={{
                                    backgroundColor: '#6b7280',
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '& .MuiChip-icon': {
                                      color: 'white',
                                      fontSize: '16px',
                                    },
                                    '& .MuiChip-label': {
                                      paddingLeft: '8px',
                                      paddingRight: '12px',
                                    },
                                    boxShadow: '0 2px 4px rgba(107, 114, 128, 0.3)',
                                    '&:hover': {
                                      backgroundColor: '#4b5563',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 4px 8px rgba(107, 114, 128, 0.4)',
                                    },
                                  }}
                                />
                              </Tooltip>
                            );
                          
                          case 'suspended':
                            return (
                              <Tooltip title="Click para reactivar suscripción" arrow>
                                <Chip
                                  icon={<CancelIcon />}
                                  label="Suspendida"
                                  size="small"
                                  clickable
                                  onClick={() => handleOpenSubscriptionModal(contact)}
                                  sx={{
                                    backgroundColor: '#6b7280',
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '& .MuiChip-icon': {
                                      color: 'white',
                                      fontSize: '16px',
                                    },
                                    '& .MuiChip-label': {
                                      paddingLeft: '8px',
                                      paddingRight: '12px',
                                    },
                                    boxShadow: '0 2px 4px rgba(107, 114, 128, 0.3)',
                                    '&:hover': {
                                      backgroundColor: '#4b5563',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 4px 8px rgba(107, 114, 128, 0.4)',
                                    },
                                  }}
                                />
                              </Tooltip>
                            );
                          
                          default: // 'none'
                            return (
                              <Tooltip title="Click para crear suscripción" arrow>
                                <Chip
                                  icon={<CancelIcon />}
                                  label="Sin suscripción"
                                  size="small"
                                  clickable
                                  onClick={() => handleOpenSubscriptionModal(contact)}
                                  sx={{
                                    backgroundColor: '#6b7280',
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '& .MuiChip-icon': {
                                      color: 'white',
                                      fontSize: '16px',
                                    },
                                    '& .MuiChip-label': {
                                      paddingLeft: '8px',
                                      paddingRight: '12px',
                                    },
                                    boxShadow: '0 2px 4px rgba(107, 114, 128, 0.3)',
                                    '&:hover': {
                                      backgroundColor: '#4b5563',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 4px 8px rgba(107, 114, 128, 0.4)',
                                    },
                                  }}
                                />
                              </Tooltip>
                            );
                        }
                      };

                      return getSubscriptionChip();
                    })()}
                  </TableCell>

                  {/* Acciones */}
                  {showActions && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(contact)}
                          sx={{
                            color: THEME_COLORS.SECONDARY,
                            '&:hover': { color: THEME_COLORS.PRIMARY },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onDelete(contact)}
                          sx={{
                            color: THEME_COLORS.SECONDARY,
                            '&:hover': { color: THEME_COLORS.ERROR },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>

                {/* Fila expandible con detalles */}
                <TableRow>
                  <TableCell colSpan={showActions ? 7 : 6} sx={{ padding: 0, border: 'none' }}>
                    <Collapse in={expandedContactId === contact.contact_id}>
                      <Box sx={{ 
                        padding: 3, 
                        backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT, 
                        borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                      }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: 2, fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY }}>
                          Detalles de {contact.nombre}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                              ID de Contacto
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                              {contact.contact_id}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                              Celular
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                              {formatCelular(contact.celular)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                              Email eWeLink
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                              {contact.email_ewelink || 'No configurado'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                              Distribuidor
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                              {contact.distributor_empresa ? `${contact.distributor_empresa} - ${contact.distributor_nombre || ''}` : 'Sin asignar'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                              Ubicación
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                              {contact.ubicacion || 'No especificada'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationControls />
      
      {/* Modal de suscripción */}
      <SubscriptionFormModal
        open={subscriptionModalOpen}
        onClose={handleCloseSubscriptionModal}
        onSubmit={handleCreateSubscription}
        entity={null}
        isEditing={false}
        availableContacts={selectedContactForSubscription ? [selectedContactForSubscription, ...availableContacts] : availableContacts}
        preselectedContact={selectedContactForSubscription}
      />
    </Box>
  );
};

export default ContactsTable; 